import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
} from "recharts";
import React from 'react'


const SupplierPerformanceChart = ({perfSummary}) => {
   const chartData = [
    { label: "Total", value: perfSummary?.total_jobs ?? 0, color: "#013760", bg: "rgba(1,55,96,0.08)" },
    { label: "Completed", value: perfSummary?.completed_jobs ?? 0, color: "#009C35", bg: "rgba(0,156,53,0.08)" },
    { label: "Pending", value: perfSummary?.pending_jobs ?? 0, color: "#EF9F27", bg: "rgba(239,159,39,0.08)" },
    { label: "In Progress", value: perfSummary?.in_progress_jobs ?? 0, color: "#596575", bg: "rgba(89,101,117,0.08)" },
    { label: "Overdue", value: perfSummary?.overdue_jobs ?? 0, color: "#CB0B03", bg: "rgba(203,11,3,0.08)" },
  ];

  const maxValue = Math.max(...chartData.map((d) => d.value), 1);
  const completionRate = perfSummary?.completion_rate ?? 0;

  // Donut chart segments
  const total = perfSummary?.total_jobs ?? 0;
  const completed = perfSummary?.completed_jobs ?? 0;
  const pending = perfSummary?.pending_jobs ?? 0;
  const inProgress = perfSummary?.in_progress_jobs ?? 0;
  const overdue = perfSummary?.overdue_jobs ?? 0;

  const donutData = [
    { value: completed, color: "#009C35" },
    { value: pending, color: "#EF9F27" },
    { value: inProgress, color: "#596575" },
    { value: overdue, color: "#CB0B03" },
  ];

  const radius = 54;
  const cx = 70;
  const cy = 70;
  const strokeWidth = 18;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  const segments = donutData.map((seg) => {
    const pct = total > 0 ? seg.value / total : 0;
    const dash = pct * circumference;
    const gap = circumference - dash;
    const strokeDasharray = `${dash} ${gap}`;
    const strokeDashoffset = -offset * circumference;
    offset += pct;
    return { ...seg, strokeDasharray, strokeDashoffset };
  });

  return (
    <div className="ct_white_bg p-4 ct_border_radius_12 ct_mb_32">
      <h3 className="ct_fs_18 ct_fw_600 mb-4 ct_ff_ProximaNova_semibold">
        Jobs Overview
      </h3>

      <div className="row align-items-center">
        {/* Donut Chart */}
        <div className="col-lg-4 col-md-5 mb-4 mb-md-0 d-flex flex-column align-items-center">
          <div style={{ position: "relative", width: 140, height: 140 }}>
            <svg width={140} height={140} viewBox="0 0 140 140">
              {/* Background circle */}
              <circle
                cx={cx}
                cy={cy}
                r={radius}
                fill="none"
                stroke="rgba(0,0,0,0.06)"
                strokeWidth={strokeWidth}
              />
              {total === 0 ? (
                <circle
                  cx={cx}
                  cy={cy}
                  r={radius}
                  fill="none"
                  stroke="rgba(0,0,0,0.1)"
                  strokeWidth={strokeWidth}
                  strokeDasharray={`${circumference} 0`}
                />
              ) : (
                segments.map((seg, i) => (
                  <circle
                    key={i}
                    cx={cx}
                    cy={cy}
                    r={radius}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={seg.strokeDasharray}
                    strokeDashoffset={seg.strokeDashoffset}
                    style={{
                      transform: "rotate(-90deg)",
                      transformOrigin: `${cx}px ${cy}px`,
                      transition: "stroke-dasharray 0.6s ease",
                    }}
                  />
                ))
              )}
            </svg>
            {/* Center text */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
              }}
            >
              <div
                className="ct_ff_ProximaNova_bold"
                style={{ fontSize: 22, color: "#013760", lineHeight: 1 }}
              >
                {completionRate}%
              </div>
              <div
                className="ct_fs_11 ct_text_para ct_ff_ProximaNova_semibold"
                style={{ marginTop: 2 }}
              >
                Done
              </div>
            </div>
          </div>

          {/* Donut legend */}
          <div className="mt-3 d-flex flex-wrap justify-content-center gap-2">
            {[
              { label: "Completed", color: "#009C35" },
              { label: "Pending", color: "#EF9F27" },
              { label: "In Progress", color: "#596575" },
              { label: "Overdue", color: "#CB0B03" },
            ].map((item, i) => (
              <div key={i} className="d-flex align-items-center gap-1">
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: item.color,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <span
                  className="ct_fs_12 ct_text_para ct_ff_ProximaNova_semibold"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Bar Chart */}
        <div className="col-lg-8 col-md-7">
          <div className="d-flex flex-column gap-3">
            {chartData.map((item, index) => {
              const barPct = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
              return (
                <div key={index}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="ct_fs_13 ct_fw_600 ct_text_para ct_ff_ProximaNova_semibold">
                      {item.label}
                    </span>
                    <span
                      className="ct_fs_13 ct_fw_600 ct_ff_ProximaNova_bold"
                      style={{ color: item.color, minWidth: 28, textAlign: "right" }}
                    >
                      {item.value}
                    </span>
                  </div>
                  <div
                    style={{
                      height: 10,
                      borderRadius: 6,
                      backgroundColor: item.bg,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${barPct}%`,
                        backgroundColor: item.color,
                        borderRadius: 6,
                        transition: "width 0.7s cubic-bezier(0.4,0,0.2,1)",
                        minWidth: item.value > 0 ? 6 : 0,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupplierPerformanceChart